import { useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useWorkspaces } from '../../../lib/workspaces';
import { useMyAgents } from '../../../lib/my-agents';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { deleteRoutine, useRoutines, type WebRoutine } from '../../../lib/routines';
import {
  agentsOf,
  clampRoutineFilters,
  filterRoutines,
  hasActiveRoutineFilters,
  spacesOf,
  type RoutineFilterState,
} from '../../../lib/routine-filters';
import { ROUTINES_PAGE } from '../../../lib/routines-bi';
import { routineConnectorSections } from '../../../lib/routine-groups';
import { queryKeys } from '../../../lib/query-client';
import { fetchConnectors } from '../../../lib/connectors';
import { useAgentName } from '../../../lib/use-agent-name';
import { Card } from '../ui';
import { RoutineCard } from './RoutineCard';
import { RoutineConnectorCard } from './RoutineConnectorCard';
import { RoutineDetailModal } from './RoutineDetailModal';
import { RoutinesFilterBar } from './RoutinesFilterBar';
import styles from './routines.module.css';

// THE one Symphony routines renderer — self-contained (fetches its own data), scoped by two axes so one
// component serves every context, exactly as `TasksBody` does for Ongoing Tasks:
//   - GLOBAL (no filter)          — the `/routines` page.
//   - PER-WORKSPACE (workspaceId) — a Space's Dashboard tab.
//   - PER-AGENT (agentId)         — arriving from an agent.
//
// `filterable` adds the two-axis filter row and is for the STANDALONE page only: an embedded host has
// already fixed its scope, and a picker for an axis the host has decided is a control that can only take
// the screen somewhere the host doesn't want it. It also changes what `workspaceId` MEANS — with
// `filterable` it is the SELECTED Space (read off `/routines?workspace=<slug>` by the page), so the list
// is fetched account-wide and narrowed here, which is what lets a user widen back to All Spaces.
export function RoutinesBody({
  workspaceId,
  agentId,
  header,
  filterable,
}: {
  workspaceId?: string;
  agentId?: string;
  header?: ReactNode;
  filterable?: boolean;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { state } = useRoutines(filterable ? undefined : workspaceId);
  const connectorsQuery = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  useLoadBi('routines-list-data', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });

  const workspacesState = useWorkspaces();
  const teamState = useMyAgents();
  const workspaceRoster = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const agentRoster = teamState.status === 'ready' ? teamState.team.agents : [];

  const all = state.kind === 'ready' ? state.routines : [];
  // A host's `workspaceId` is applied server-side; `agentId` narrows client-side.
  const scoped = all.filter((routine) => !agentId || routine.agentId === agentId);

  // Options are DERIVED FROM THE ROUTINES IN SCOPE, never from the full roster, so a menu can never
  // offer a value that matches nothing. An axis the host has fixed offers nothing at all, which is how
  // the bar knows not to draw a picker for it.
  const spaceOptions = filterable ? spacesOf(scoped, workspaceRoster) : [];
  const agentOptions = filterable && !agentId ? agentsOf(scoped, agentRoster, t('routines.filter.noAgent')) : [];

  // The Space lives in the URL — the view stays shareable and the back button works — while the agent
  // axis is this component's own. Both are clamped once the data AND both rosters have landed: clamping
  // against nothing would silently widen a shared `?workspace=` link to every Space for a frame.
  const [agentPick, setAgentPick] = useState('');
  const requested: RoutineFilterState = { workspaceId: filterable ? (workspaceId ?? '') : '', agentId: agentPick };
  const resolved =
    state.kind === 'ready' && workspacesState.status === 'ready' && teamState.status === 'ready';
  const filters = resolved ? clampRoutineFilters(requested, spaceOptions, agentOptions) : requested;
  const visible = filterRoutines(scoped, filters);

  // The screen is grouped by CONNECTOR, one card each, in the connectors screen's own order so the two
  // agree. Filtering happens first and the grouping is applied to what survived: a connector whose
  // routines were all filtered out has no card, rather than an empty one saying the filter found nothing
  // here specifically.
  //
  // The list waits for the connectors read as well as the routines one. The connectors decide the cards'
  // ORDER and their titles, the two queries land in whichever order the network gives them, and
  // routines-first is the common case — which would lay the cards out and then reorder and rename them a
  // moment later. Waiting for BOTH costs one skeleton frame.
  //
  // An ERROR does not wait, though — a connectors read that will never succeed must not hold the
  // routines screen empty forever. The cards are then ordered and titled from the slugs alone, which is
  // the honest answer when we cannot ask the connectors screen what an app is called.
  const connectorsSettled = connectorsQuery.status !== 'loading';
  const connectorOrder = (connectorsQuery.data?.connectors ?? [])
    .filter((connector) => connector.connected === true)
    .map((connector) => (connector.toolkit ?? '').toLowerCase())
    .filter(Boolean);
  const sections = routineConnectorSections(visible, connectorOrder);

  const spaceName = (id?: string): string | undefined => {
    // With the Space axis fixed by the host or by the picker, naming it on every card says nothing.
    if (!filterable || filters.workspaceId) return undefined;
    const wid = id || workspaceId;
    return wid ? workspaceRoster.find((w) => w.id === wid)?.name : undefined;
  };

  const filterBi = (name: string) =>
    logPageClickBi({ ...ROUTINES_PAGE, button_name: name }, { componentName: name.replace(/_/g, '-') });

  const applyFilters = (next: RoutineFilterState) => {
    setAgentPick(next.agentId);
    if (next.workspaceId === filters.workspaceId) return;
    const slug = workspaceRoster.find((w) => w.id === next.workspaceId)?.slug;
    const query = { ...router.query };
    if (slug) query.workspace = slug;
    else delete query.workspace;
    void router.push({ pathname: router.pathname, query }, undefined, { shallow: true });
  };

  // One drill-in at a time, held as an ID and resolved from the list on every render — never as a
  // snapshot of the entity. A cached object would go stale the moment the modal's own switch or edit
  // patched the cache, which is exactly how the WhatsApp group modal used to spring back.
  const [openId, setOpenId] = useState<string | null>(null);
  const [openInEdit, setOpenInEdit] = useState(false);
  const open = visible.find((routine) => routine.id === openId) ?? null;
  const openDetail = (id: string, edit: boolean) => {
    setOpenInEdit(edit);
    setOpenId(id);
  };

  const chatAbout = (routine: WebRoutine, name: string) => {
    const link = chatHandoffLink({ kind: 'new', message: t('routines.chatSeed', { name }) }, basePath);
    void router.push(link.route, link.path);
  };

  const empty = state.kind === 'ready' && scoped.length === 0;

  return (
    <div className={styles.body}>
      {header}
      {!empty && filterable && state.kind === 'ready' && (
        <RoutinesFilterBar
          className={styles.filters}
          spaces={spaceOptions}
          agents={agentOptions}
          filters={filters}
          onChange={applyFilters}
          onBi={filterBi}
        />
      )}

      {(state.kind === 'loading' || (state.kind === 'ready' && !empty && !connectorsSettled)) && <LoadingSkeleton />}
      {state.kind === 'error' && <div className={styles.error}>{state.message}</div>}
      {/* An account with NO routines does not wait on the connectors read — there is nothing to group. */}
      {state.kind === 'ready' &&
        (empty || connectorsSettled) &&
        (empty ? (
          <EmptyState />
        ) : visible.length === 0 ? (
          <Card className={styles.empty}>
            {/* An account with routines that has been narrowed to none has not run out of routines —
                saying "No routines yet" there is untrue and hides the filter that emptied the screen. */}
            <div className={styles.emptyHint}>
              {t(hasActiveRoutineFilters(filters) ? 'routines.filter.noMatch' : 'routines.empty.title')}
            </div>
          </Card>
        ) : (
          <div className={styles.list}>
            {sections.map((section) => (
              <RoutineConnectorCard key={section.slug ?? 'own'} slug={section.slug} count={section.routines.length}>
                {section.routines.map((routine) => (
                  <RoutineCard
                    key={routine.id}
                    routine={routine}
                    flat
                    spaceName={spaceName(routine.workspaceId)}
                    onOpen={() => openDetail(routine.id, false)}
                    onEdit={() => openDetail(routine.id, true)}
                    onChat={() => chatAbout(routine, routine.name?.trim() || t('routines.untitled'))}
                    onDelete={() => void deleteRoutine(routine.id)}
                  />
                ))}
              </RoutineConnectorCard>
            ))}
          </div>
        ))}

      {open && (
        <RoutineDetailModal
          key={open.id}
          routine={open}
          startInEdit={openInEdit}
          spaceName={workspaceRoster.find((w) => w.id === open.workspaceId)?.name}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  const agent = useAgentName();
  return (
    <Card className={styles.empty}>
      <div className={styles.emptyTitle}>{t('routines.empty.title')}</div>
      <div className={styles.emptyHint}>{t('routines.empty.hint', { maestroAgentName: agent.short })}</div>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className={styles.list}>
      {[70, 55, 62].map((w, i) => (
        <Card key={i} size="sm" className={styles.skelCard}>
          <div className="orion-shimmer" style={{ height: 14, width: `${w}%` }} />
          <div className="orion-shimmer" style={{ height: 10, width: `${w - 20}%`, marginTop: 10 }} />
          <div className="orion-shimmer" style={{ height: 10, width: `${w - 30}%`, marginTop: 16 }} />
        </Card>
      ))}
    </div>
  );
}
